// Besoins et irritants d'un persona.
//
// L'IA peut en PROPOSER 3 à 5 à partir du métier et de la séniorité (moteur
// repris de Filon). Ces propositions sont là pour guider la saisie : chacune
// porte la marque « IA » tant qu'elle existe, dans le formulaire comme sur la
// carte. Ce ne sont pas des constats — les preuves restent les verbatims.
import { useProposePersonaNeeds } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { PersonaNeed, Seniority } from '../api/types';
import { Button, Chip, ErrorBanner, IconButton, Select } from '../components/ui';
import './personas.css';

export const SENIORITIES: { value: Seniority; label: string }[] = [
  { value: 'debutant', label: 'Débutant' },
  { value: 'confirme', label: 'Confirmé' },
  { value: 'expert', label: 'Expert' },
  { value: 'senior', label: 'Sénior' },
];

export function seniorityLabel(v: Seniority | null | undefined): string | null {
  return SENIORITIES.find((s) => s.value === v)?.label ?? null;
}

export function IaTag() {
  return (
    <Chip ia title="Proposé par l’IA pour guider la saisie — à confirmer sur le terrain">
      IA
    </Chip>
  );
}

export function PersonaNeedsEditor({
  projectId,
  role,
  seniority,
  needs,
  onChange,
}: {
  projectId: string;
  role: string;
  seniority: Seniority | null;
  needs: PersonaNeed[];
  onChange: (next: PersonaNeed[]) => void;
}) {
  const propose = useProposePersonaNeeds(projectId);
  const hasIa = needs.some((n) => n.origin === 'ia');

  async function run() {
    try {
      const res = await propose.mutateAsync({ role: role.trim(), seniority });
      // Une nouvelle proposition REMPLACE la précédente, jamais ce qui a été
      // saisi à la main.
      onChange([...needs.filter((n) => n.origin !== 'ia'), ...res.items]);
    } catch {
      /* rendu par le bandeau */
    }
  }

  const set = (i: number, patch: Partial<PersonaNeed>) =>
    onChange(needs.map((n, j) => (j === i ? { ...n, ...patch } : n)));

  return (
    <fieldset className="needs-editor">
      <legend className="zy-field__label">Besoins et irritants</legend>

      <div className="needs-ai">
        <div>
          <p className="needs-ai-title">Proposer avec l’IA</p>
          <p className="zy-texte-muet">
            {role.trim()
              ? `3 à 5 besoins et irritants typiques d’un profil « ${role.trim()} »${seniority ? ` ${seniorityLabel(seniority)?.toLowerCase()}` : ''}. Des pistes pour guider la saisie, pas des constats : à confirmer sur le terrain.`
              : 'Renseignez d’abord le rôle du persona : la proposition part du métier.'}
          </p>
        </div>
        <Button type="button" variant="ghost" loading={propose.isPending} disabled={!role.trim()} onClick={() => void run()}>
          {hasIa ? 'Proposer à nouveau' : 'Proposer'}
        </Button>
      </div>
      {propose.error ? (
        <ErrorBanner>{propose.error instanceof ApiRequestError ? propose.error.message : 'La proposition a échoué.'}</ErrorBanner>
      ) : null}

      {needs.length > 0 ? (
        <ul className="needs-list">
          {needs.map((n, i) => (
            <li key={i} className={`need-row need-${n.type}`}>
              <Select
                className="need-type"
                value={n.type}
                onChange={(e) => set(i, { type: e.target.value as PersonaNeed['type'] })}
                aria-label={`Nature de l’élément ${i + 1}`}
              >
                <option value="besoin">Besoin</option>
                <option value="irritant">Irritant</option>
              </Select>
              <input
                className="zy-input"
                value={n.label}
                onChange={(e) => set(i, { label: e.target.value })}
                placeholder={n.type === 'besoin' ? 'Savoir en un coup d’œil ce qui est disponible' : 'Ressaisir au bureau ce qui a été noté sur le terrain'}
                aria-label={`${n.type === 'besoin' ? 'Besoin' : 'Irritant'} ${i + 1}`}
                autoFocus={n.label === '' && n.origin === 'manuel'}
              />
              {n.origin === 'ia' ? <IaTag /> : <span aria-hidden="true" />}
              <IconButton icon="close" danger
                className="feature-remove"
                onClick={() => onChange(needs.filter((_, j) => j !== i))}
                label={`Retirer l’élément ${i + 1}`} />
            </li>
          ))}
        </ul>
      ) : null}

      <div className="needs-add">
        <Button variant="ghost" size="s" icon="plus" type="button" className="feature-add" onClick={() => onChange([...needs, { type: 'besoin', label: '', origin: 'manuel' }])}>
          Besoin
        </Button>
        <Button variant="ghost" size="s" icon="plus" type="button" className="feature-add" onClick={() => onChange([...needs, { type: 'irritant', label: '', origin: 'manuel' }])}>
          Irritant
        </Button>
      </div>
    </fieldset>
  );
}

/** Lecture sur la carte du persona : besoins d'un côté, irritants de l'autre. */
export function PersonaNeedsView({ needs }: { needs: PersonaNeed[] }) {
  if (needs.length === 0) return null;
  const groups: [PersonaNeed['type'], string][] = [['besoin', 'Besoins'], ['irritant', 'Irritants']];
  const anyIa = needs.some((n) => n.origin === 'ia');
  return (
    <div className="needs-view">
      {groups.map(([type, title]) => {
        const list = needs.filter((n) => n.type === type);
        if (list.length === 0) return null;
        return (
          <div key={type} className={`needs-view-group need-${type}`}>
            <p className="needs-view-title">{title}</p>
            <ul>
              {list.map((n, i) => (
                <li key={i}>
                  <span>{n.label}</span>
                  {n.origin === 'ia' ? <IaTag /> : null}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {anyIa ? (
        <p className="needs-view-note">
          <IaTag /> Proposé par l’IA pour guider — à confirmer sur le terrain.
        </p>
      ) : null}
    </div>
  );
}
